import type Lenis from 'lenis'

export interface TrackOptions {
    /** Viewport fraction (0 = top, 1 = bottom) the element's top must reach for progress 0. */
    startAt?: number
    /** Viewport fraction the element's bottom must reach for progress 1. */
    endAt?: number
    /** CSS custom property written on the element. Pass null to skip writing. */
    varName?: string | null
    onUpdate?: (progress: number) => void
}

interface Tracker {
    el: HTMLElement
    startAt: number
    endAt: number
    varName: string | null
    onUpdate?: (progress: number) => void
    progress: number
    last: number
}

type FrameListener = (scrollY: number, viewportHeight: number) => void

const trackers = new Set<Tracker>()
const frameListeners = new Set<FrameListener>()

let lenis: Lenis | null = null
let rafId = 0
let running = false
let dirty = true
let lastY = Number.NaN
let lastVh = Number.NaN
let lastVw = Number.NaN
let resizeObserver: ResizeObserver | null = null
let reducedMotion = false

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const tick = (time: number): void => {
    if (lenis) lenis.raf(time)

    const y = window.scrollY
    const vh = window.innerHeight
    const vw = window.innerWidth

    if (y !== lastY || vh !== lastVh || vw !== lastVw) {
        dirty = true
        lastY = y
        lastVh = vh
        lastVw = vw
    }

    if (dirty) {
        dirty = false

        trackers.forEach(tracker => {
            const rect = tracker.el.getBoundingClientRect()
            const span = (tracker.startAt - tracker.endAt) * vh + rect.height
            const raw = span > 0 ? (tracker.startAt * vh - rect.top) / span : 0

            tracker.progress = clamp01(raw)
        })

        trackers.forEach(tracker => {
            if (tracker.progress === tracker.last) return

            tracker.last = tracker.progress

            if (tracker.varName) tracker.el.style.setProperty(tracker.varName, tracker.progress.toFixed(4))
            if (tracker.onUpdate) tracker.onUpdate(tracker.progress)
        })

        frameListeners.forEach(listener => listener(y, vh))
    }

    rafId = window.requestAnimationFrame(tick)
}

export const markDirty = (): void => {
    dirty = true
}

export const isReducedMotion = (): boolean => reducedMotion

export const getLenis = (): Lenis | null => lenis

export const startEngine = async (): Promise<() => void> => {
    if (running || typeof window === 'undefined') return () => undefined

    running = true
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (!reducedMotion) {
        try {
            const { default: LenisClass } = await import('lenis')

            lenis = new LenisClass({
                lerp: 0.11,
                smoothWheel: true,
                wheelMultiplier: 1,
                anchors: { offset: -80 },
                stopInertiaOnNavigate: true
            })
        }
        catch (error) {
            console.error('Smooth scrolling could not start', error)
            lenis = null
        }
    }

    resizeObserver = new ResizeObserver(() => markDirty())
    resizeObserver.observe(document.documentElement)

    window.addEventListener('load', markDirty)
    document.fonts?.ready.then(markDirty).catch(() => undefined)

    rafId = window.requestAnimationFrame(tick)

    return () => {
        window.cancelAnimationFrame(rafId)
        window.removeEventListener('load', markDirty)
        resizeObserver?.disconnect()
        resizeObserver = null
        lenis?.destroy()
        lenis = null
        running = false
    }
}

export const track = (el: HTMLElement, options: TrackOptions = {}): (() => void) => {
    const tracker: Tracker = {
        el,
        startAt: options.startAt ?? 1,
        endAt: options.endAt ?? 0,
        varName: options.varName === undefined ? '--p' : options.varName,
        onUpdate: options.onUpdate,
        progress: 0,
        last: -1
    }

    trackers.add(tracker)
    markDirty()

    return () => {
        trackers.delete(tracker)
    }
}

export const onFrame = (listener: FrameListener): (() => void) => {
    frameListeners.add(listener)
    markDirty()

    return () => {
        frameListeners.delete(listener)
    }
}

export const scrollToTop = (): void => {
    if (lenis) {
        lenis.scrollTo(0, { immediate: true, force: true })
        return
    }

    window.scrollTo(0, 0)
}

export const scrollToTarget = (target: HTMLElement | number, offset = -80): void => {
    if (lenis) {
        lenis.scrollTo(target, { offset, duration: 1.4 })
        return
    }

    const top = typeof target === 'number'
        ? target
        : target.getBoundingClientRect().top + window.scrollY + offset

    window.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' })
}

export const stopScroll = (): void => {
    lenis?.stop()
}

export const resumeScroll = (): void => {
    lenis?.start()
}
